import { useState } from "react";
import axios from "axios";
import { useAuthContext } from "./useAuthContext";
import { useAccountContext } from "./useAccountContext";
import { useSettingsContext } from "./useSettingsContext";

export const useEmploymentContext = () => {
  const { user, clientApi } = useAuthContext();
  const [contextData, setContextData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { account } = useAccountContext();
  const { isWinAuth } = useSettingsContext();

  const axiosInstance = axios.create({
    baseURL: `${clientApi}`,
    headers: {
      accepts: "application/json",
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.request.use(
    (request) => {
      const accessToken = user.token;
      if (accessToken) {
        request.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          if (!isWinAuth) {
            // Make a request to your auth server to refresh the token.
            const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
            // Store the new access and refresh tokens.
            authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

            // Update the authorization header with the new access token.
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          else {
            const newToken = await acquireSilentToken();
            let tmpUser = user;
            tmpUser.token = newToken;
            authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );

  const getEmploymentContextData = async (selectedCompany) => {
    if (!selectedCompany) return [];
    console.log("passed");
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${clientApi}api/Company/EmployementContext`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { company: selectedCompany },
        }
      );
      setContextData(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching employment context data:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const updateContext = async (contexts, updateSucc) => {
    console.log(contexts);
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Company/PutEmployementContext`,
        contexts,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { employeeid: account.employeeId },
        }
      );

      setContextData(response.data);
      return true;
    } catch (err) {
      console.error("Error updating employment context:", err);
      updateSucc = false;
      return false;
    }
  };

  const insertContext = async (contexts, insertSucc) => {
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Company/InsertEmployementContext`,
        contexts,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { employeeid: account.employeeId },
        }
      );
      console.log("Employment context inserted successfully:", response.data);
      return true;
    } catch (err) {
      console.error("Error inserting employment context:", err.message);
      return false;
    }
  };

  const deleteContext = async (contexts, deleteSucc) => {
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Company/DeleteEmployementContext`,
        contexts,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { employeeid: account.employeeId },
        }
      );

      console.log("Employment context deleted successfully:", response.data);
      return true;
    } catch (err) {
      console.error("Error deleting employment context:", err);
      return false;
    }
  };

  return {
    error,
    getEmploymentContextData,
    updateContext,
    insertContext,
    deleteContext,
  };
};